import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Send,
  FileText,
  Inbox,
  RefreshCw,
  Search,
  Star,
  Trash2,
  Archive,
  MoreHorizontal,
  ChevronDown,
  Clock,
  Check,
  AlertCircle,
  X,
  Tag,
  FileEdit,
  MessageSquare,
  Hash,
  Loader2,
  ArrowLeft,
  Reply,
  Forward,
  Paperclip,
  Users,
  AtSign,
  SkipForward,
  Sparkles,
  RotateCcw,
  CheckCircle2,
  Settings,
} from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';
import { useWorkspaceStore } from '../store/workspace';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Types
interface Integration {
  id: number;
  provider: string;
  provider_username?: string;
  provider_email?: string;
  status: string;
}

interface Email {
  id: string;
  threadId: string;
  from: string;
  fromName?: string;
  fromEmail: string;
  to: string;
  subject: string;
  snippet: string;
  body?: string;
  date: string;
  timestamp: string;
  isUnread: boolean;
  isStarred?: boolean;
  labels: string[];
  draftId?: string;
}

// Slack Types
interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
  isArchived: boolean;
  isMember: boolean;
  memberCount?: number;
  topic?: string;
  purpose?: string;
}

interface SlackMessage {
  id: string;
  userId?: string;
  botId?: string;
  text: string;
  timestamp: string;
  threadTs?: string;
  replyCount?: number;
  reactions?: Array<{ name: string; count: number }>;
}

interface SlackUser {
  id: string;
  name: string;
  realName?: string;
  displayName?: string;
  email?: string;
  avatar?: string;
}

// Discord Types
interface DiscordGuild {
  id: string;
  name: string;
  icon: string | null;
  isOwner: boolean;
  memberCount?: number;
  onlineCount?: number;
  features: string[];
}

interface DiscordChannel {
  id: string;
  name: string;
  type: string;
  topic?: string | null;
  position?: number;
  parentId?: string | null;
  nsfw?: boolean;
}

interface DiscordMessage {
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    globalName?: string | null;
    avatar: string | null;
    isBot?: boolean;
  };
  timestamp: string;
  editedAt?: string | null;
  isPinned: boolean;
  hasAttachments: boolean;
  attachmentCount: number;
  embedCount: number;
  mentionCount: number;
  isReply: boolean;
  replyTo?: {
    id: string;
    content: string;
    authorUsername: string;
  } | null;
}

interface DiscordMember {
  id: string;
  username: string;
  globalName?: string | null;
  nickname?: string | null;
  avatar: string | null;
  isBot?: boolean;
  joinedAt: string;
  roles: string[];
  isPending?: boolean;
}

// API Response Types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface ConnectResponse {
  auth_url?: string;
  requires_setup?: boolean;
}

// Email content formatting helper
const formatEmailContent = (content: string): string => {
  if (!content) return '';

  // Check if content is already HTML (has common HTML tags)
  const isHtml = /<\/?(?:div|p|br|span|table|tr|td|a|img|ul|ol|li|h[1-6]|strong|em|b|i)[^>]*>/i.test(content);

  if (isHtml) {
    // For HTML content, enhance styling for links and ensure proper formatting
    let formatted = content;

    // Style links that don't already have inline styles
    formatted = formatted.replace(
      /<a(?![^>]*style=)([^>]*)href="([^"]*)"([^>]*)>/gi,
      '<a$1href="$2"$3 style="color: #3b82f6; text-decoration: underline;">'
    );

    // Ensure links open in new tab for security
    formatted = formatted.replace(
      /<a(?![^>]*target=)([^>]*)>/gi,
      '<a$1 target="_blank" rel="noopener noreferrer">'
    );

    return formatted;
  }

  // For plain text content, convert to formatted HTML
  let formatted = content;

  // Escape HTML entities to prevent XSS
  formatted = formatted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Convert URLs to clickable links with styling
  const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
  formatted = formatted.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline; word-break: break-all;">$1</a>'
  );

  // Convert email addresses to mailto links
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
  formatted = formatted.replace(
    emailRegex,
    '<a href="mailto:$1" style="color: #3b82f6; text-decoration: underline;">$1</a>'
  );

  // Convert double newlines to paragraph breaks
  formatted = formatted.replace(/\n\n+/g, '</p><p style="margin: 12px 0;">');

  // Convert single newlines to line breaks
  formatted = formatted.replace(/\n/g, '<br>');

  // Wrap in paragraph tags
  formatted = '<p style="margin: 0;">' + formatted + '</p>';

  // Clean up empty paragraphs
  formatted = formatted.replace(/<p[^>]*>\s*<\/p>/g, '');

  return formatted;
};

// Onboarding Steps Components
const AgentAvatar = ({ size = 'large' }: { size?: 'large' | 'small' }) => {
  const dimensions = size === 'large' ? 'w-16 h-16' : 'w-8 h-8';
  const innerDimensions = size === 'large' ? 'w-14 h-14' : 'w-7 h-7';
  const fontSize = size === 'large' ? 'text-2xl' : 'text-sm';

  return (
    <div className={`${dimensions} rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-lg`}>
      <div className={`${innerDimensions} rounded-full bg-gradient-to-br from-sky-300 to-blue-400 flex items-center justify-center`}>
        <span className={fontSize}>🤖</span>
      </div>
    </div>
  );
};

interface EmailPreviewProps {
  sender: string;
  label: string;
  labelColor: string;
  subject: string;
  hasIcon?: boolean;
}

const EmailPreview = ({ sender, label, labelColor, subject, hasIcon }: EmailPreviewProps) => (
  <div className="flex items-center gap-2 sm:gap-3 py-2 sm:py-2.5 px-2 sm:px-3 bg-white rounded-lg border border-gray-100 overflow-hidden">
    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 accent-blue-500 flex-shrink-0 hidden sm:block" disabled />
    <span className="text-xs sm:text-sm text-gray-800 font-medium w-16 sm:w-24 truncate flex-shrink-0">{sender}</span>
    <span
      className="px-1.5 sm:px-2 py-0.5 rounded text-xs font-semibold text-white whitespace-nowrap flex-shrink-0"
      style={{ backgroundColor: labelColor }}
    >
      {label}
    </span>
    {hasIcon && <span className="text-sm flex-shrink-0">☀️</span>}
    <span className="text-xs sm:text-sm text-gray-600 truncate flex-1 min-w-0">{subject}</span>
  </div>
);

const LabelChip = ({ color, text }: { color: string; text: string }) => (
  <span
    className="px-3 py-1.5 rounded-full text-sm font-medium text-white shadow-sm"
    style={{ backgroundColor: color }}
  >
    {text}
  </span>
);

const CategoryItem = ({ color, text, count }: { color: string; text: string; count?: number }) => (
  <div className="flex items-center gap-2">
    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></span>
    <span className="text-sm text-gray-700">{text}</span>
    {count !== undefined && <span className="text-sm text-gray-500">({count})</span>}
  </div>
);

// Main Communication Component
export default function Communication() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const { token } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL parameters for deep linking from Inbox
  const urlPlatform = searchParams.get('platform') as 'gmail' | 'slack' | 'discord' | null;
  const urlEmailId = searchParams.get('email');
  const urlThreadId = searchParams.get('thread');
  const urlChannelId = searchParams.get('channel');

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [hasConnectedCommunication, setHasConnectedCommunication] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);

  // Gmail state
  const [activeTab, setActiveTab] = useState<'inbox' | 'drafts' | 'sent'>('inbox');
  const [emails, setEmails] = useState<Email[]>([]);
  const [drafts, setDrafts] = useState<Email[]>([]);
  const [sentEmails, setSentEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Compose state
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');

  // AI Reply state
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [generatedReply, setGeneratedReply] = useState('');
  const [showReplyComposer, setShowReplyComposer] = useState(false);

  // Ref to prevent auto-scroll when reply composer opens
  const emailContentRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Platform selector state
  const [activePlatform, setActivePlatform] = useState<'gmail' | 'slack' | 'discord'>('gmail');

  // Slack state
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [slackMessages, setSlackMessages] = useState<SlackMessage[]>([]);
  const [slackUsers, setSlackUsers] = useState<SlackUser[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<SlackChannel | null>(null);
  const [isLoadingSlack, setIsLoadingSlack] = useState(false);
  const [slackSearchQuery, setSlackSearchQuery] = useState('');
  const [slackMessageText, setSlackMessageText] = useState('');
  const [selectedThread, setSelectedThread] = useState<SlackMessage | null>(null);
  const [selectedSlackMessage, setSelectedSlackMessage] = useState<SlackMessage | null>(null);
  const [showSlackChannelDropdown, setShowSlackChannelDropdown] = useState(false);

  // Discord state
  const [discordGuilds, setDiscordGuilds] = useState<DiscordGuild[]>([]);
  const [discordChannels, setDiscordChannels] = useState<DiscordChannel[]>([]);
  const [discordMessages, setDiscordMessages] = useState<DiscordMessage[]>([]);
  const [discordMembers, setDiscordMembers] = useState<DiscordMember[]>([]);
  const [selectedGuild, setSelectedGuild] = useState<DiscordGuild | null>(null);
  const [selectedDiscordChannel, setSelectedDiscordChannel] = useState<DiscordChannel | null>(null);
  const [isLoadingDiscord, setIsLoadingDiscord] = useState(false);
  const [discordSearchQuery, setDiscordSearchQuery] = useState('');
  const [discordMessageText, setDiscordMessageText] = useState('');
  const [selectedDiscordMessage, setSelectedDiscordMessage] = useState<DiscordMessage | null>(null);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showDiscordGuildDropdown, setShowDiscordGuildDropdown] = useState(false);
  const [showDiscordChannelDropdown, setShowDiscordChannelDropdown] = useState(false);

  // Settings panel state
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [connectedGmailEmail, setConnectedGmailEmail] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);

  // Check for connected communication integrations
  const checkIntegrations = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<Integration[]>;

      if (data.success && data.data) {
        setIntegrations(data.data);
        // Check if Gmail, Slack, or Discord is connected
        const communicationProviders = ['gmail', 'slack', 'discord'];
        const connected = data.data.some(
          (i) =>
            communicationProviders.includes(i.provider) && i.status === 'connected'
        );
        setHasConnectedCommunication(connected);

        const hasGmail = data.data.some((i) => i.provider === 'gmail' && i.status === 'connected');
        const hasSlack = data.data.some((i) => i.provider === 'slack' && i.status === 'connected');
        const hasDiscord = data.data.some((i) => i.provider === 'discord' && i.status === 'connected');

        // Set default platform based on what's connected
        if (hasGmail) {
          setActivePlatform('gmail');
          loadEmails();
        } else if (hasSlack) {
          setActivePlatform('slack');
          loadSlackChannels();
        } else if (hasDiscord) {
          setActivePlatform('discord');
          loadDiscordGuilds();
        }
      }
    } catch (error) {
      console.error('Failed to check integrations:', error);
    }
    setIsLoading(false);
  }, [token, currentWorkspace?.id]);

  useEffect(() => {
    checkIntegrations();
  }, [checkIntegrations]);

  // Load Gmail emails
  const loadEmails = async (type: 'inbox' | 'drafts' | 'sent' = 'inbox') => {
    if (!token) return;

    setIsLoadingEmails(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `&workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/gmail/messages?type=${type}${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<Email[]>;

      if (data.success && data.data) {
        if (type === 'inbox') {
          setEmails(data.data);
        } else if (type === 'drafts') {
          setDrafts(data.data);
        } else if (type === 'sent') {
          setSentEmails(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to load emails:', error);
    }
    setIsLoadingEmails(false);
  };

  // Load custom instructions for email replies
  const loadCustomInstructions = async () => {
    if (!token) return;

    setIsLoadingSettings(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/gmail/settings${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<{ custom_instructions: string; connected_email: string }>;

      if (data.success && data.data) {
        setCustomInstructions(data.data.custom_instructions || '');
        setConnectedGmailEmail(data.data.connected_email || '');
      }
    } catch (error) {
      console.error('Failed to load custom instructions:', error);
    }
    setIsLoadingSettings(false);
  };

  // Save custom instructions
  const saveCustomInstructions = async () => {
    if (!token) return;

    setIsSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/communication/gmail/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          custom_instructions: customInstructions,
          workspace_id: currentWorkspace?.id,
        }),
      });
      const data = await response.json() as ApiResponse<{ success: boolean }>;

      if (data.success) {
        // Close the settings panel after saving
        setShowSettingsPanel(false);
      }
    } catch (error) {
      console.error('Failed to save custom instructions:', error);
    }
    setIsSavingSettings(false);
  };

  // Reset custom instructions
  const resetCustomInstructions = async () => {
    setCustomInstructions('');
  };

  // Handle tab change
  const handleTabChange = (tab: 'inbox' | 'drafts' | 'sent') => {
    setActiveTab(tab);
    setSelectedEmail(null);
    loadEmails(tab);
  };

  // Connect to provider
  const connectProvider = async (provider: string) => {
    if (!token) return;

    setConnectingProvider(provider);
    try {
      const response = await fetch(`${API_URL}/integrations/connect/${provider}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: currentWorkspace?.id,
          redirect_url: '/dashboard/communication',
        }),
      });

      const data = await response.json() as ApiResponse<ConnectResponse>;
      if (data.success && data.data?.auth_url) {
        window.location.href = data.data.auth_url;
      }
    } catch (error) {
      console.error('Failed to connect provider:', error);
    }
    setConnectingProvider(null);
  };

  // Send reply
  const sendReply = async () => {
    if (!token || !selectedEmail || !replyContent.trim()) return;

    // Extract recipient email - try fromEmail first, then parse from 'from' field
    let recipientEmail = selectedEmail.fromEmail;
    if (!recipientEmail && selectedEmail.from) {
      // Parse email from "Name <email@domain.com>" format
      const emailMatch = selectedEmail.from.match(/<([^>]+)>/);
      recipientEmail = emailMatch ? emailMatch[1] : selectedEmail.from;
    }

    if (!recipientEmail) {
      console.error('Could not determine recipient email');
      return;
    }

    setIsSending(true);
    try {
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (currentWorkspace?.id) {
        headers['X-Workspace-Id'] = String(currentWorkspace.id);
      }

      const response = await fetch(`${API_URL}/communication/gmail/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          threadId: selectedEmail.threadId,
          messageId: selectedEmail.id,
          to: recipientEmail,
          subject: `Re: ${selectedEmail.subject}`,
          body: replyContent,
          workspace_id: currentWorkspace?.id,
        }),
      });

      const data = await response.json() as ApiResponse<unknown>;
      if (data.success) {
        setReplyContent('');
        setIsReplying(false);
        setShowReplyComposer(false);
        setGeneratedReply('');
        // Refresh emails
        loadEmails('inbox');
      } else {
        console.error('Failed to send reply:', data.error);
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
    }
    setIsSending(false);
  };

  // Send new email
  const sendEmail = async () => {
    if (!token || !composeTo.trim() || !composeSubject.trim() || !composeBody.trim()) return;

    setIsSending(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/gmail/send${workspaceParam}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: composeTo,
          subject: composeSubject,
          body: composeBody,
        }),
      });

      const data = await response.json() as ApiResponse<unknown>;
      if (data.success) {
        setComposeTo('');
        setComposeSubject('');
        setComposeBody('');
        setIsComposing(false);
        // Refresh sent emails
        loadEmails('sent');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
    }
    setIsSending(false);
  };

  // Generate AI reply
  const generateAIReply = async () => {
    if (!token || !selectedEmail) return;

    // Save scroll position before showing reply composer
    if (emailContentRef.current) {
      scrollPositionRef.current = emailContentRef.current.scrollTop;
    }

    setIsGeneratingReply(true);
    setShowReplyComposer(true);
    setGeneratedReply('');

    // Restore scroll position after DOM update
    requestAnimationFrame(() => {
      if (emailContentRef.current) {
        emailContentRef.current.scrollTop = scrollPositionRef.current;
      }
    });

    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/gmail/generate-reply${workspaceParam}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailId: selectedEmail.id,
          from: selectedEmail.from,
          fromName: selectedEmail.fromName,
          fromEmail: selectedEmail.fromEmail,
          subject: selectedEmail.subject,
          body: selectedEmail.body || selectedEmail.snippet,
        }),
      });

      const data = await response.json() as ApiResponse<{ reply: string }>;
      if (data.success && data.data?.reply) {
        setGeneratedReply(data.data.reply);
        setReplyContent(data.data.reply);
      } else {
        // Fallback to a simple generated reply if API fails
        const fallbackReply = `Hi ${selectedEmail.fromName || selectedEmail.from.split('<')[0].trim()},\n\nThank you for your email. I've reviewed your message and will get back to you with a detailed response shortly.\n\nBest regards`;
        setGeneratedReply(fallbackReply);
        setReplyContent(fallbackReply);
      }
    } catch (error) {
      console.error('Failed to generate AI reply:', error);
      // Fallback reply
      const fallbackReply = `Hi ${selectedEmail.fromName || selectedEmail.from.split('<')[0].trim()},\n\nThank you for your email. I've reviewed your message and will get back to you with a detailed response shortly.\n\nBest regards`;
      setGeneratedReply(fallbackReply);
      setReplyContent(fallbackReply);
    }
    setIsGeneratingReply(false);

    // Restore scroll position after reply is generated
    requestAnimationFrame(() => {
      if (emailContentRef.current) {
        emailContentRef.current.scrollTop = scrollPositionRef.current;
      }
    });
  };

  // Skip email (move to next)
  const skipEmail = () => {
    if (!selectedEmail) return;
    const currentEmails = activeTab === 'inbox' ? emails : activeTab === 'drafts' ? drafts : sentEmails;
    const currentIndex = currentEmails.findIndex(e => e.id === selectedEmail.id);
    if (currentIndex < currentEmails.length - 1) {
      setSelectedEmail(currentEmails[currentIndex + 1]);
    } else if (currentEmails.length > 0) {
      setSelectedEmail(currentEmails[0]);
    }
    setShowReplyComposer(false);
    setGeneratedReply('');
    setReplyContent('');
  };

  // Get email category label based on content analysis
  const getEmailCategory = (email: Email): { label: string; color: string } => {
    const subject = email.subject.toLowerCase();
    const from = email.from.toLowerCase();
    const snippet = email.snippet?.toLowerCase() || '';

    // Check for cold email patterns
    if (subject.includes('offer') || subject.includes('deal') || subject.includes('discount') ||
        subject.includes('sale') || subject.includes('limited time') || subject.includes('free')) {
      return { label: 'Cold Email', color: '#3b82f6' };
    }

    // Check for notifications
    if (from.includes('noreply') || from.includes('no-reply') || from.includes('notification') ||
        subject.includes('notification') || subject.includes('alert') || subject.includes('update')) {
      return { label: 'Notification', color: '#22c55e' };
    }

    // Check for marketing
    if (subject.includes('newsletter') || subject.includes('subscribe') || from.includes('marketing')) {
      return { label: 'Marketing', color: '#ec4899' };
    }

    // Check for meeting/calendar
    if (subject.includes('meeting') || subject.includes('calendar') || subject.includes('schedule') ||
        subject.includes('appointment') || subject.includes('invite')) {
      return { label: 'Meeting', color: '#8b5cf6' };
    }

    // Check if needs response (questions, requests)
    if (subject.includes('?') || snippet.includes('?') || subject.includes('request') ||
        subject.includes('help') || subject.includes('urgent') || subject.includes('asap')) {
      return { label: 'To Respond', color: '#ef4444' };
    }

    // Check for FYI type emails
    if (subject.includes('fyi') || subject.includes('info') || subject.includes('sharing') ||
        subject.includes('fyi') || subject.includes('just to let you know')) {
      return { label: 'FYI', color: '#facc15' };
    }

    // Default - needs review
    return { label: 'To Respond', color: '#ef4444' };
  };

  // ============================================
  // Slack Functions
  // ============================================

  // Load Slack channels
  const loadSlackChannels = async () => {
    if (!token) return;

    setIsLoadingSlack(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/slack/channels${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<SlackChannel[]>;

      if (data.success && data.data) {
        setSlackChannels(data.data);
      }
    } catch (error) {
      console.error('Failed to load Slack channels:', error);
    }
    setIsLoadingSlack(false);
  };

  // Load Slack messages for a channel
  const loadSlackMessages = async (channelId: string) => {
    if (!token) return;

    setIsLoadingSlack(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `&workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/slack/messages?channel_id=${channelId}${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<SlackMessage[]>;

      if (data.success && data.data) {
        setSlackMessages(data.data);
      }
    } catch (error) {
      console.error('Failed to load Slack messages:', error);
    }
    setIsLoadingSlack(false);
  };

  // Load Slack users
  const loadSlackUsers = async () => {
    if (!token) return;

    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/slack/users${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<SlackUser[]>;

      if (data.success && data.data) {
        setSlackUsers(data.data);
      }
    } catch (error) {
      console.error('Failed to load Slack users:', error);
    }
  };

  // Handle channel selection
  const handleChannelSelect = (channel: SlackChannel) => {
    setSelectedChannel(channel);
    setSelectedThread(null);
    loadSlackMessages(channel.id);
    loadSlackUsers();
  };

  // Send Slack message
  const sendSlackMessage = async () => {
    if (!token || !selectedChannel || !slackMessageText.trim()) return;

    setIsSending(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const endpoint = selectedThread
        ? `${API_URL}/communication/slack/reply${workspaceParam}`
        : `${API_URL}/communication/slack/send${workspaceParam}`;

      const body = selectedThread
        ? { channel: selectedChannel.id, thread_ts: selectedThread.id, text: slackMessageText }
        : { channel: selectedChannel.id, text: slackMessageText };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json() as ApiResponse<unknown>;
      if (data.success) {
        setSlackMessageText('');
        setSelectedThread(null);
        // Refresh messages
        loadSlackMessages(selectedChannel.id);
      }
    } catch (error) {
      console.error('Failed to send Slack message:', error);
    }
    setIsSending(false);
  };

  // Get user display name from ID
  const getSlackUserName = (userId?: string) => {
    if (!userId) return 'Bot';
    const user = slackUsers.find((u) => u.id === userId);
    return user?.displayName || user?.realName || user?.name || 'Unknown';
  };

  // Handle platform switch
  const handlePlatformSwitch = (platform: 'gmail' | 'slack' | 'discord') => {
    setActivePlatform(platform);
    if (platform === 'slack' && slackChannels.length === 0) {
      loadSlackChannels();
    } else if (platform === 'discord' && discordGuilds.length === 0) {
      loadDiscordGuilds();
    }
  };

  // ============================================
  // Discord Functions
  // ============================================

  // Load Discord guilds (servers)
  const loadDiscordGuilds = async () => {
    if (!token) return;

    setIsLoadingDiscord(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/discord/guilds${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<DiscordGuild[]>;

      if (data.success && data.data) {
        setDiscordGuilds(data.data);
      }
    } catch (error) {
      console.error('Failed to load Discord guilds:', error);
    }
    setIsLoadingDiscord(false);
  };

  // Load Discord channels for a guild
  const loadDiscordChannels = async (guildId: string) => {
    if (!token) return;

    setIsLoadingDiscord(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `&workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/discord/channels?guild_id=${guildId}${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<DiscordChannel[]>;

      if (data.success && data.data) {
        setDiscordChannels(data.data);
      }
    } catch (error) {
      console.error('Failed to load Discord channels:', error);
    }
    setIsLoadingDiscord(false);
  };

  // Load Discord messages for a channel
  const loadDiscordMessages = async (channelId: string) => {
    if (!token) return;

    setIsLoadingDiscord(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `&workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/discord/messages?channel_id=${channelId}${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<DiscordMessage[]>;

      if (data.success && data.data) {
        setDiscordMessages(data.data);
      }
    } catch (error) {
      console.error('Failed to load Discord messages:', error);
    }
    setIsLoadingDiscord(false);
  };

  // Load Discord guild members
  const loadDiscordMembers = async (guildId: string) => {
    if (!token) return;

    try {
      const workspaceParam = currentWorkspace?.id ? `&workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/communication/discord/members?guild_id=${guildId}${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as ApiResponse<DiscordMember[]>;

      if (data.success && data.data) {
        setDiscordMembers(data.data);
      }
    } catch (error) {
      console.error('Failed to load Discord members:', error);
    }
  };

  // Handle guild selection
  const handleGuildSelect = (guild: DiscordGuild) => {
    setSelectedGuild(guild);
    setSelectedDiscordChannel(null);
    setDiscordMessages([]);
    setDiscordMembers([]);
    loadDiscordChannels(guild.id);
    loadDiscordMembers(guild.id);
  };

  // Handle channel selection
  const handleDiscordChannelSelect = (channel: DiscordChannel) => {
    setSelectedDiscordChannel(channel);
    setSelectedDiscordMessage(null);
    loadDiscordMessages(channel.id);
  };

  // Send Discord message
  const sendDiscordMessage = async () => {
    if (!token || !selectedDiscordChannel || !discordMessageText.trim()) return;

    setIsSending(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';

      // Check if replying to a message
      const endpoint = selectedDiscordMessage
        ? `${API_URL}/communication/discord/reply${workspaceParam}`
        : `${API_URL}/communication/discord/send${workspaceParam}`;

      const body = selectedDiscordMessage
        ? { channel_id: selectedDiscordChannel.id, message_id: selectedDiscordMessage.id, content: discordMessageText }
        : { channel_id: selectedDiscordChannel.id, content: discordMessageText };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json() as ApiResponse<unknown>;
      if (data.success) {
        setDiscordMessageText('');
        setSelectedDiscordMessage(null);
        // Refresh messages
        loadDiscordMessages(selectedDiscordChannel.id);
      }
    } catch (error) {
      console.error('Failed to send Discord message:', error);
    }
    setIsSending(false);
  };

  // Get integration info
  const gmailIntegration = integrations.find(i => i.provider === 'gmail' && i.status === 'connected');
  const slackIntegration = integrations.find(i => i.provider === 'slack' && i.status === 'connected');
  const discordIntegration = integrations.find(i => i.provider === 'discord' && i.status === 'connected');

  // Count connected platforms
  const connectedPlatforms = [gmailIntegration, slackIntegration, discordIntegration].filter(Boolean).length;

  // Onboarding Step 1
  const OnboardingStep1 = () => (
    <div className="text-center px-4 sm:px-6 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
        Turn on Inbox Manager
      </h1>
      <p className="text-gray-500 mb-6 sm:mb-10 max-w-lg mx-auto leading-relaxed text-sm sm:text-base">
        Your AI Employee helps you handle your inbox smarter — labels what matters, marks what's done, and drafts replies ready for your review.
      </p>

      <div className="bg-sky-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 max-w-2xl mx-auto shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
          <div className="hidden sm:flex flex-col items-center min-w-[140px]">
            <AgentAvatar size="large" />
            <div className="mt-6 space-y-3 text-left w-full">
              <CategoryItem color="#ef4444" text="To Respond" count={2} />
              <CategoryItem color="#facc15" text="FYI" count={1} />
              <CategoryItem color="#3b82f6" text="Cold outreach" />
              <CategoryItem color="#22c55e" text="Notification" />
            </div>
          </div>

          <div className="flex-1 space-y-2 min-w-0">
            <EmailPreview sender="Sophie Tran" label="To Respond" labelColor="#ef4444" subject="Follow-up on the proc..." />
            <EmailPreview sender="Daily Digest" label="Notification" labelColor="#22c55e" subject="Morning insights a..." hasIcon />
            <EmailPreview sender="Marta Li" label="FYI" labelColor="#facc15" subject="Team lunch schedule — Fr..." />
            <EmailPreview sender="Ethan Cole" label="To Respond" labelColor="#ef4444" subject="Need approval: marke..." />
            <EmailPreview sender="Hang Mill" label="Cold outreach" labelColor="#3b82f6" subject="Automate your busi..." />
            <EmailPreview sender="TechMart" label="Notification" labelColor="#22c55e" subject="Your invoice #48291..." />
          </div>
        </div>
      </div>

      <button
        onClick={() => setOnboardingStep(1)}
        className="mt-6 sm:mt-10 px-8 sm:px-10 py-3 sm:py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-500/25"
      >
        Get started
      </button>
    </div>
  );

  // Onboarding Step 2
  const OnboardingStep2 = () => (
    <div className="text-center px-4 sm:px-6 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
        Organize your Inbox
      </h1>
      <p className="text-gray-500 mb-6 sm:mb-10 max-w-lg mx-auto leading-relaxed text-sm sm:text-base">
        Your AI Employee adds labels so you instantly can see what needs your attention — without digging through clutter.
      </p>

      <div className="bg-sky-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 max-w-2xl mx-auto shadow-sm overflow-hidden">
        <div className="flex flex-wrap justify-center gap-2 mb-4 sm:mb-6">
          <LabelChip color="#ef4444" text="To Respond" />
          <LabelChip color="#facc15" text="FYI" />
          <LabelChip color="#22c55e" text="Cold Email" />
          <LabelChip color="#f97316" text="Awaiting Reply" />
          <LabelChip color="#ec4899" text="Marketing" />
          <LabelChip color="#8b5cf6" text="Meeting Update" />
          <LabelChip color="#10b981" text="Actioned" />
        </div>

        <div className="space-y-2 bg-white/60 rounded-2xl p-3 sm:p-4 min-w-0">
          <EmailPreview sender="Sophie Tran" label="To Respond" labelColor="#ef4444" subject="Follow-up on the product mockups" />
          <EmailPreview sender="Marta Li" label="FYI" labelColor="#facc15" subject="Team lunch schedule — Friday at 1 PM" />
          <EmailPreview sender="Daily Digest" label="Marketing" labelColor="#ec4899" subject="Morning insights and headlines" hasIcon />
        </div>
      </div>

      <div className="mt-6 sm:mt-8 flex items-center justify-center gap-3 sm:gap-4 bg-white rounded-2xl p-4 sm:p-5 max-w-md mx-auto shadow-sm border border-gray-100">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Tag className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
        </div>
        <div className="text-left min-w-0">
          <p className="font-semibold text-gray-900 text-base sm:text-lg">Smart labels</p>
          <p className="text-xs sm:text-sm text-gray-500">Your AI Employee will automatically categorize incoming emails</p>
        </div>
      </div>

      <button
        onClick={() => setOnboardingStep(2)}
        className="mt-6 sm:mt-10 px-8 sm:px-10 py-3 sm:py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-500/25"
      >
        Continue
      </button>
    </div>
  );

  // Onboarding Step 3
  const OnboardingStep3 = () => (
    <div className="text-center px-4 sm:px-6 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
        Reply faster to emails
      </h1>
      <p className="text-gray-500 mb-6 sm:mb-10 max-w-xl mx-auto leading-relaxed text-sm sm:text-base">
        Your AI Employee uses your Brain AI context to write draft replies in your tone. You'll review and send them — nothing leaves your inbox without your approval.
      </p>

      <div className="bg-sky-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 max-w-2xl mx-auto shadow-sm overflow-hidden">
        <div className="bg-gray-100 rounded-2xl p-4 sm:p-5 mb-4 text-left text-sm text-gray-700 leading-relaxed">
          <p>If this looks good, please tell me which package you'd like. I'll prepare the agreement so we can finalize before the end of the month.</p>
          <p className="mt-3">Thanks, really excited to keep supporting your business growth.</p>
          <p className="mt-3">Best,</p>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
            <AgentAvatar size="small" />
            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500 min-w-0 flex-1">
              <span className="text-gray-400">↩</span>
              <span>To</span>
              <span className="text-gray-800 font-medium truncate">client@company.com</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs sm:text-sm text-gray-400">Cc Bcc</span>
            </div>
          </div>

          <div className="text-left text-xs sm:text-sm text-gray-700 leading-relaxed space-y-3">
            <p>Hi there,</p>
            <p>Thanks for putting this together so clearly. Let's move forward with option 2 - the Business plan. Please go ahead and prepare the agreement and we'll get it signed before the deadline.</p>
            <p>Best,<br />Your Name</p>
          </div>

          <div className="flex items-center gap-3 mt-4 sm:mt-5 pt-4 border-t border-gray-100">
            <button className="px-4 sm:px-5 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm">
              Send <span className="text-xs opacity-70">▼</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 flex items-center justify-center gap-3 sm:gap-4 bg-white rounded-2xl p-4 sm:p-5 max-w-md mx-auto shadow-sm border border-gray-100">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <FileEdit className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
        </div>
        <div className="text-left min-w-0">
          <p className="font-semibold text-gray-900 text-base sm:text-lg">Smart drafts</p>
          <p className="text-xs sm:text-sm text-gray-500">Draft replies appear in both SweetPilot and your inbox</p>
        </div>
      </div>

      <button
        onClick={() => setOnboardingStep(3)}
        className="mt-6 sm:mt-10 px-8 sm:px-10 py-3 sm:py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-500/25"
      >
        Continue
      </button>
    </div>
  );

  // Onboarding Step 4 - Connect
  const OnboardingStep4 = () => (
    <div className="text-center px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
        Connect your account
      </h1>
      <p className="text-gray-500 mb-8 sm:mb-10 text-sm sm:text-base">
        Choose your communication platform to get started
      </p>

      <div className="max-w-md mx-auto space-y-3 sm:space-y-4">
        {/* Gmail */}
        <div className="flex items-center justify-between bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 hover:border-gray-200 transition-colors">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-red-50 flex-shrink-0">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-gray-900 text-base sm:text-lg block">Gmail</span>
              <span className="text-xs sm:text-sm text-gray-500">Manage your email inbox</span>
            </div>
          </div>
          <button
            onClick={() => connectProvider('gmail')}
            disabled={connectingProvider === 'gmail'}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm sm:text-base font-semibold rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {connectingProvider === 'gmail' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Connect'
            )}
          </button>
        </div>

        {/* Slack */}
        <div className="flex items-center justify-between bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 hover:border-gray-200 transition-colors">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-purple-50 flex-shrink-0">
              <Hash className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-gray-900 text-base sm:text-lg block">Slack</span>
              <span className="text-xs sm:text-sm text-gray-500">Send messages to channels</span>
            </div>
          </div>
          <button
            onClick={() => connectProvider('slack')}
            disabled={connectingProvider === 'slack'}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm sm:text-base font-semibold rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {connectingProvider === 'slack' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Connect'
            )}
          </button>
        </div>

        {/* Discord */}
        <div className="flex items-center justify-between bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 hover:border-gray-200 transition-colors">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-indigo-50 flex-shrink-0">
              <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-500" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-gray-900 text-base sm:text-lg block">Discord</span>
              <span className="text-xs sm:text-sm text-gray-500">Send messages to servers</span>
            </div>
          </div>
          <button
            onClick={() => connectProvider('discord')}
            disabled={connectingProvider === 'discord'}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm sm:text-base font-semibold rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {connectingProvider === 'discord' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Connect'
            )}
          </button>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-2 mt-8 sm:mt-10">
        {[0, 1, 2, 3].map((step) => (
          <div
            key={step}
            className={`w-2 h-2 rounded-full transition-colors ${
              step === 3 ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );

  // Email Dashboard Component - Sintra.ai inspired simple inbox design
  const EmailDashboard = () => {
    const currentEmails = activeTab === 'inbox' ? emails : activeTab === 'drafts' ? drafts : sentEmails;
    const [showInboxDropdown, setShowInboxDropdown] = useState(false);

    // Get tab display name
    const getTabDisplayName = () => {
      switch (activeTab) {
        case 'inbox': return 'Inbox';
        case 'sent': return 'Sent';
        case 'drafts': return 'Drafts';
        default: return 'Inbox';
      }
    };

    // Format date for display
    const formatEmailDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    // Get sender name from email
    const getSenderName = (email: Email) => {
      if (email.fromName) return email.fromName;
      const from = email.from;
      if (from.includes('<')) {
        return from.split('<')[0].trim();
      }
      return from.split('@')[0];
    };

    // Get avatar color based on first letter
    const getAvatarColor = (name: string) => {
      const colors = [
        'bg-blue-500',
        'bg-green-500',
        'bg-purple-500',
        'bg-pink-500',
        'bg-orange-500',
        'bg-teal-500',
        'bg-indigo-500',
        'bg-rose-500',
      ];
      const index = name.charCodeAt(0) % colors.length;
      return colors[index];
    };

    return (
      <div className="flex flex-col sm:flex-row h-[calc(100vh-64px)] pt-4 sm:pt-6 px-3 sm:px-6 gap-4 sm:gap-6 overflow-hidden">
        {/* Left Panel - Inbox List (Sintra.ai simple style) */}
        <div className={`flex flex-col min-h-0 ${selectedEmail ? 'hidden sm:flex sm:w-[520px] sm:flex-shrink-0' : 'flex-1 max-w-2xl mx-auto w-full'}`}>
          {/* Header */}
          <div className="py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <button className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-white/60'}`}>
                <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
              </button>

              {/* Inbox dropdown selector */}
              <div className="relative">
                <button
                  onClick={() => setShowInboxDropdown(!showInboxDropdown)}
                  className={`flex items-center gap-2 text-lg sm:text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  {getTabDisplayName()}
                  <ChevronDown className={`w-4 sm:w-5 h-4 sm:h-5 transition-transform ${showInboxDropdown ? 'rotate-180' : ''} ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
                </button>

                {/* Dropdown menu */}
                {showInboxDropdown && (
                  <div className={`absolute top-full left-0 mt-2 w-40 rounded-xl shadow-lg py-1 z-50 ${isDark ? 'bg-dark-700 border border-border' : 'bg-white border border-gray-200'}`}>
                    <button
                      onClick={() => {
                        handleTabChange('inbox');
                        setShowInboxDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        activeTab === 'inbox'
                          ? isDark ? 'bg-dark-600 text-white' : 'bg-blue-50 text-blue-600'
                          : isDark ? 'text-text-secondary hover:bg-dark-600' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Inbox className="w-4 h-4" />
                      Inbox
                    </button>
                    <button
                      onClick={() => {
                        handleTabChange('sent');
                        setShowInboxDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        activeTab === 'sent'
                          ? isDark ? 'bg-dark-600 text-white' : 'bg-blue-50 text-blue-600'
                          : isDark ? 'text-text-secondary hover:bg-dark-600' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Send className="w-4 h-4" />
                      Sent
                    </button>
                    <button
                      onClick={() => {
                        handleTabChange('drafts');
                        setShowInboxDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        activeTab === 'drafts'
                          ? isDark ? 'bg-dark-600 text-white' : 'bg-blue-50 text-blue-600'
                          : isDark ? 'text-text-secondary hover:bg-dark-600' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      Drafts
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Platform Switcher - show when multiple platforms are connected */}
              {connectedPlatforms >= 2 && (
                <div className={`hidden sm:flex gap-1 p-1 rounded-xl mr-2 ${isDark ? 'bg-dark-700' : 'bg-white/60'}`}>
                  {gmailIntegration && (
                    <button
                      onClick={() => handlePlatformSwitch('gmail')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activePlatform === 'gmail'
                          ? isDark ? 'bg-dark-600 text-white' : 'bg-white text-gray-900 shadow-sm'
                          : isDark ? 'text-text-muted hover:text-white' : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Gmail
                    </button>
                  )}
                  {slackIntegration && (
                    <button
                      onClick={() => handlePlatformSwitch('slack')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activePlatform === 'slack'
                          ? isDark ? 'bg-dark-600 text-white' : 'bg-white text-gray-900 shadow-sm'
                          : isDark ? 'text-text-muted hover:text-white' : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <Hash className="w-4 h-4" />
                      Slack
                    </button>
                  )}
                  {discordIntegration && (
                    <button
                      onClick={() => handlePlatformSwitch('discord')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activePlatform === 'discord'
                          ? isDark ? 'bg-dark-600 text-white' : 'bg-white text-gray-900 shadow-sm'
                          : isDark ? 'text-text-muted hover:text-white' : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Discord
                    </button>
                  )}
                </div>
              )}
              <button
                onClick={() => loadEmails(activeTab)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-white/60 text-gray-500'}`}
              >
                <RefreshCw className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
              <button
                onClick={() => {
                  setShowSettingsPanel(true);
                  loadCustomInstructions();
                }}
                className={`p-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-white/80 hover:bg-white text-gray-700 border border-gray-200/50 shadow-sm'}`}
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Settings</span>
              </button>
            </div>
          </div>

          {/* Email List - Simple card style like Sintra.ai */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-sm`}>
              {isLoadingEmails ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : currentEmails.length === 0 ? (
                <div className="text-center py-16">
                  <Inbox className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    No {activeTab} emails
                  </p>
                </div>
              ) : (
                currentEmails
                  .filter(email =>
                    email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    email.from.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((email, index, arr) => {
                    const category = getEmailCategory(email);
                    const senderName = getSenderName(email);
                    return (
                      <button
                        key={email.id}
                        onClick={async () => {
                          setSelectedEmail(email);
                          setShowReplyComposer(false);
                          setGeneratedReply('');
                          setReplyContent('');

                          // Mark email as read if it's unread
                          if (email.isUnread && token) {
                            try {
                              const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
                              await fetch(`${API_URL}/communication/gmail/messages/${email.id}/mark${wsParam}`, {
                                method: 'POST',
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ action: 'read' }),
                              });

                              // Update local state to reflect the change
                              setEmails(prevEmails =>
                                prevEmails.map(e =>
                                  e.id === email.id ? { ...e, isUnread: false } : e
                                )
                              );
                            } catch (error) {
                              console.error('Failed to mark email as read:', error);
                            }
                          }
                        }}
                        className={`w-full px-3 sm:px-5 py-3 sm:py-4 text-left transition-all ${
                          selectedEmail?.id === email.id
                            ? isDark ? 'bg-dark-700' : 'bg-blue-50'
                            : isDark ? 'hover:bg-dark-700/50' : 'hover:bg-gray-50'
                        } ${index !== arr.length - 1 ? (isDark ? 'border-b border-dark-700' : 'border-b border-gray-100') : ''}`}
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          {/* Avatar */}
                          <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full ${getAvatarColor(senderName)} flex items-center justify-center text-white text-sm sm:text-base font-semibold flex-shrink-0`}>
                            {senderName.charAt(0).toUpperCase()}
                          </div>

                          <div className="flex-1 min-w-0">
                            {/* Sender name and date */}
                            <div className="flex items-center justify-between mb-0.5">
                              <span className={`font-semibold text-sm sm:text-base truncate mr-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {senderName}
                              </span>
                              <span className={`text-xs sm:text-sm flex-shrink-0 ${isDark ? 'text-text-muted' : 'text-gray-400'}`}>
                                {formatEmailDate(email.timestamp || email.date)}
                              </span>
                            </div>

                            {/* Subject */}
                            <p className={`font-medium text-sm sm:text-base mb-1 truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {email.subject}
                            </p>

                            {/* Preview snippet */}
                            <p className={`text-xs sm:text-sm truncate mb-2 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                              {email.snippet}
                            </p>

                            {/* Category label at bottom */}
                            <span
                              className="inline-block px-2 sm:px-2.5 py-0.5 sm:py-1 rounded text-xs font-semibold text-white"
                              style={{ backgroundColor: category.color }}
                            >
                              {category.label}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Email Detail (Sintra.ai style) */}
        {selectedEmail && (
          <div className={`fixed sm:relative inset-0 sm:inset-auto sm:w-[620px] flex-shrink-0 flex flex-col sm:ml-auto sm:min-h-0 sm:h-full ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-xl sm:rounded-2xl z-50 sm:z-auto overflow-hidden`}>
            {/* Close button header */}
            <div className="flex items-center justify-between px-4 sm:px-6 pt-4">
              <button
                onClick={() => setSelectedEmail(null)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}
              >
                <ArrowLeft className={`w-5 h-5 sm:hidden ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
                <X className={`w-5 h-5 hidden sm:block ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
              </button>
              <a
                href={`https://mail.google.com/mail/u/0/#inbox/${selectedEmail.threadId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-gray-100 text-gray-400'}`}
                title="Open in Gmail"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Email Content */}
            <div ref={emailContentRef} className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
              {/* Subject title */}
              <h1 className={`text-lg sm:text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {selectedEmail.subject}
              </h1>

              {/* Category label */}
              {(() => {
                const category = getEmailCategory(selectedEmail);
                return (
                  <span
                    className="inline-block px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-semibold text-white mb-4 sm:mb-6"
                    style={{ backgroundColor: category.color }}
                  >
                    {category.label}
                  </span>
                );
              })()}

              {/* Sender info card */}
              <div className={`rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 ${isDark ? 'bg-dark-700' : 'bg-gray-50'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${getAvatarColor(getSenderName(selectedEmail))} flex items-center justify-center text-white text-sm sm:text-base font-semibold flex-shrink-0`}>
                    {getSenderName(selectedEmail).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 flex-wrap">
                      <span className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {getSenderName(selectedEmail)}
                      </span>
                      <span className={`hidden sm:inline ${isDark ? 'text-text-muted' : 'text-gray-400'}`}>|</span>
                      <span className={`text-xs sm:text-sm truncate ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                        {selectedEmail.fromEmail || selectedEmail.from}
                      </span>
                      <span className={`text-xs sm:text-sm sm:ml-auto ${isDark ? 'text-text-muted' : 'text-gray-400'}`}>
                        {new Date(selectedEmail.timestamp || selectedEmail.date).toLocaleDateString([], { month: 'short', day: 'numeric' })}, {new Date(selectedEmail.timestamp || selectedEmail.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                      To {selectedEmail.to || 'me'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Email body */}
              <div
                className={`email-content prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''} ${isDark ? 'text-text-secondary' : 'text-gray-700'}`}
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  lineHeight: '1.7',
                  fontSize: '14px',
                }}
                dangerouslySetInnerHTML={{
                  __html: formatEmailContent(selectedEmail.body || selectedEmail.snippet || '')
                }}
              />
              <style>{`
                .email-content a {
                  color: #3b82f6 !important;
                  text-decoration: underline !important;
                }
                .email-content a:hover {
                  color: #2563eb !important;
                }
                .email-content p {
                  margin: 12px 0;
                }
                .email-content p:first-child {
                  margin-top: 0;
                }
                .email-content blockquote,
                .email-content .quote-block {
                  border-left: 3px solid #6b7280;
                  padding-left: 12px;
                  margin: 8px 0;
                  color: #6b7280;
                }
                .email-content img {
                  max-width: 100%;
                  height: auto;
                }
                .email-content table {
                  border-collapse: collapse;
                  width: 100%;
                }
                .email-content td, .email-content th {
                  padding: 8px;
                  border: 1px solid ${isDark ? '#374151' : '#e5e7eb'};
                }
              `}</style>

              {/* AI Reply Composer */}
              {showReplyComposer && (
                <div className={`p-4 sm:p-6 border-t ${isDark ? 'border-border' : 'border-gray-100'}`}>
                  {/* Header - stack vertically on mobile */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-base sm:text-lg">📧</span>
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Reply generated by Echo
                        </p>
                        <p className={`text-xs truncate ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                          To: {selectedEmail.fromEmail || selectedEmail.from}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={generateAIReply}
                      disabled={isGeneratingReply}
                      className={`sm:ml-auto flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-sm transition-colors w-full sm:w-auto ${
                        isDark
                          ? 'bg-dark-700 hover:bg-dark-600 text-text-secondary'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      } disabled:opacity-50`}
                    >
                      {isGeneratingReply ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4" />
                      )}
                      Regenerate
                    </button>
                  </div>

                  {/* Reply textarea - full width */}
                  {isGeneratingReply ? (
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-gray-50'} min-h-[100px] sm:min-h-[120px] flex items-center justify-center`}>
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto mb-2" />
                        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                          Echo is writing a reply...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Echo's reply will appear here..."
                      rows={6}
                      className={`w-full p-3 sm:p-4 rounded-xl border resize-none text-sm sm:text-base min-h-[150px] sm:min-h-[120px] ${
                        isDark
                          ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                    />
                  )}

                  {/* Action buttons - stack on mobile */}
                  <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 mt-4">
                    <button
                      onClick={() => {
                        setShowReplyComposer(false);
                        setGeneratedReply('');
                        setReplyContent('');
                      }}
                      className={`px-5 py-2.5 rounded-xl font-medium transition-colors text-center ${
                        isDark
                          ? 'bg-dark-700 hover:bg-dark-600 text-text-secondary'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendReply}
                      disabled={isSending || !replyContent.trim()}
                      className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                    >
                      {isSending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Send
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom action bar - Skip & Review Reply (Sintra.ai style) */}
            {!showReplyComposer && (
              <div className={`p-4 border-t ${isDark ? 'border-border' : 'border-gray-100'}`}>
                <div className="flex items-center gap-3">
                  <button
                    onClick={skipEmail}
                    className={`flex-1 py-3 rounded-xl font-medium text-center transition-colors ${
                      isDark
                        ? 'bg-dark-700 hover:bg-dark-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Skip
                  </button>
                  <button
                    onClick={generateAIReply}
                    disabled={isGeneratingReply}
                    className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                  >
                    {isGeneratingReply ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : null}
                    Review reply
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compose Modal */}
        <AnimatePresence>
          {isComposing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setIsComposing(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className={`w-full max-w-2xl rounded-2xl shadow-2xl ${isDark ? 'bg-dark-800' : 'bg-white'}`}
              >
                <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-border' : 'border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    New Message
                  </h3>
                  <button
                    onClick={() => setIsComposing(false)}
                    className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}
                  >
                    <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <input
                      type="email"
                      placeholder="To"
                      value={composeTo}
                      onChange={(e) => setComposeTo(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        isDark
                          ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Subject"
                      value={composeSubject}
                      onChange={(e) => setComposeSubject(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        isDark
                          ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                  <div>
                    <textarea
                      placeholder="Write your message..."
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      rows={10}
                      className={`w-full px-4 py-3 rounded-xl border resize-none ${
                        isDark
                          ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>

                <div className={`flex items-center justify-between p-4 border-t ${isDark ? 'border-border' : 'border-gray-200'}`}>
                  <button className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}>
                    <Paperclip className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
                  </button>
                  <button
                    onClick={sendEmail}
                    disabled={isSending || !composeTo.trim() || !composeSubject.trim() || !composeBody.trim()}
                    className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Settings Panel (Sintra.ai style) */}
        <AnimatePresence>
          {showSettingsPanel && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 h-full w-[400px] z-50 shadow-2xl flex flex-col ${isDark ? 'bg-dark-800' : 'bg-white'}`}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-border' : 'border-gray-200'}`}>
                <button
                  onClick={() => setShowSettingsPanel(false)}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
                </button>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Settings</h2>
                <div className="w-9" /> {/* Spacer for alignment */}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Connected Account */}
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Connected account</h3>
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {connectedGmailEmail || gmailIntegration?.provider_email || gmailIntegration?.provider_username || 'Gmail Account'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Instructions */}
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Custom Instructions</h3>
                  {isLoadingSettings ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : (
                    <textarea
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      placeholder="I am concise in my communication, polite but direct. I prefer shorter emails. If asked for help or guidance, I prefer to respond with bullet points for ease of reading. Always sign-off with Best, [Your Name]"
                      rows={6}
                      className={`w-full p-4 rounded-xl border resize-none text-sm ${
                        isDark
                          ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                          : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  )}
                </div>

                {/* Help Button */}
                <button
                  className={`w-full p-4 rounded-xl flex items-center gap-3 transition-colors ${
                    isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">?</span>
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Get Help</span>
                </button>

                {/* Reset Button */}
                <button
                  onClick={resetCustomInstructions}
                  className={`w-full p-4 rounded-xl flex items-center gap-3 transition-colors ${
                    isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                    <RotateCcw className="w-4 h-4 text-white" />
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Reset</span>
                </button>
              </div>

              {/* Save Button */}
              <div className="p-6">
                <button
                  onClick={saveCustomInstructions}
                  disabled={isSavingSettings}
                  className="w-full py-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSavingSettings ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Settings Panel Overlay */}
        {showSettingsPanel && (
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setShowSettingsPanel(false)}
          />
        )}
      </div>
    );
  };

  // Slack Dashboard Component (Gmail-style layout)
  const SlackDashboard = () => {
    const filteredChannels = slackChannels.filter((channel) =>
      channel.name.toLowerCase().includes(slackSearchQuery.toLowerCase())
    );

    // Format message date
    const formatMessageDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    // Get avatar color based on user name
    const getAvatarColor = (name: string) => {
      const colors = [
        'bg-purple-500',
        'bg-indigo-500',
        'bg-blue-500',
        'bg-green-500',
        'bg-pink-500',
        'bg-orange-500',
        'bg-teal-500',
        'bg-rose-500',
      ];
      const index = name.charCodeAt(0) % colors.length;
      return colors[index];
    };

    return (
      <div className="flex h-[calc(100vh-64px)] pt-6 px-6 gap-6 overflow-hidden">
        {/* Left Panel - Message List (Gmail-style) */}
        <div className={`flex flex-col min-h-0 ${selectedSlackMessage ? 'w-[520px] flex-shrink-0' : 'flex-1 max-w-2xl mx-auto'}`}>
          {/* Header */}
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (selectedSlackMessage) {
                    setSelectedSlackMessage(null);
                  } else if (selectedChannel) {
                    setSelectedChannel(null);
                    setSlackMessages([]);
                  }
                }}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-white/60'} ${!selectedChannel && !selectedSlackMessage ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!selectedChannel && !selectedSlackMessage}
              >
                <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
              </button>

              {/* Channel dropdown selector */}
              <div className="relative">
                <button
                  onClick={() => setShowSlackChannelDropdown(!showSlackChannelDropdown)}
                  className={`flex items-center gap-2 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  <Hash className="w-5 h-5" />
                  {selectedChannel?.name || 'Select Channel'}
                  <ChevronDown className={`w-5 h-5 transition-transform ${showSlackChannelDropdown ? 'rotate-180' : ''} ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
                </button>

                {/* Dropdown menu */}
                {showSlackChannelDropdown && (
                  <div className={`absolute top-full left-0 mt-2 w-64 rounded-xl shadow-lg py-1 z-50 max-h-80 overflow-y-auto ${isDark ? 'bg-dark-700 border border-border' : 'bg-white border border-gray-200'}`}>
                    {/* Search in dropdown */}
                    <div className={`px-3 py-2 border-b ${isDark ? 'border-border' : 'border-gray-100'}`}>
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-dark-600' : 'bg-gray-100'}`}>
                        <Search className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
                        <input
                          type="text"
                          placeholder="Search channels..."
                          value={slackSearchQuery}
                          onChange={(e) => setSlackSearchQuery(e.target.value)}
                          className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder-text-muted' : 'text-gray-900 placeholder-gray-400'}`}
                        />
                      </div>
                    </div>
                    {isLoadingSlack && slackChannels.length === 0 ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                      </div>
                    ) : filteredChannels.length === 0 ? (
                      <p className={`text-sm text-center py-4 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                        No channels found
                      </p>
                    ) : (
                      filteredChannels.map((channel) => (
                        <button
                          key={channel.id}
                          onClick={() => {
                            handleChannelSelect(channel);
                            setShowSlackChannelDropdown(false);
                            setSelectedSlackMessage(null);
                          }}
                          className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                            selectedChannel?.id === channel.id
                              ? isDark ? 'bg-dark-600 text-white' : 'bg-purple-50 text-purple-600'
                              : isDark ? 'text-text-secondary hover:bg-dark-600' : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Hash className="w-4 h-4" />
                          <span className="truncate">{channel.name}</span>
                          {channel.isPrivate && <span className="ml-auto text-xs opacity-50">🔒</span>}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadSlackChannels}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-white/60 text-gray-500'}`}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Message List - Card style */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-sm`}>
              {!selectedChannel ? (
                <div className="text-center py-16">
                  <Hash className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    Select a channel to view messages
                  </p>
                </div>
              ) : isLoadingSlack ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : slackMessages.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    No messages in this channel
                  </p>
                </div>
              ) : (
                [...slackMessages].reverse().map((message) => {
                  const userName = getSlackUserName(message.userId);
                  const isSelected = selectedSlackMessage?.id === message.id;

                  return (
                    <div
                      key={message.id}
                      onClick={() => setSelectedSlackMessage(message)}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b ${
                        isDark ? 'border-dark-700' : 'border-gray-100'
                      } ${
                        isSelected
                          ? isDark ? 'bg-dark-700' : 'bg-purple-50'
                          : isDark ? 'hover:bg-dark-700/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full ${getAvatarColor(userName)} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}>
                        {userName.charAt(0).toUpperCase()}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {userName}
                          </span>
                          <span className={`text-xs flex-shrink-0 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                            {formatMessageDate(message.timestamp)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${isDark ? 'text-text-secondary' : 'text-gray-600'}`}>
                          {message.text}
                        </p>
                        {message.replyCount && message.replyCount > 0 && (
                          <span className={`text-xs mt-1 inline-block ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                            {message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Message input at bottom of left panel */}
          {selectedChannel && (
            <div className={`mt-4 p-4 rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-sm`}>
              {selectedThread && (
                <div className={`flex items-center gap-2 mb-2 text-xs ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                  <Reply className="w-3 h-3" />
                  <span>Replying to thread</span>
                  <button onClick={() => setSelectedThread(null)} className="ml-auto hover:underline">
                    Cancel
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder={`Message #${selectedChannel.name}`}
                  value={slackMessageText}
                  onChange={(e) => setSlackMessageText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendSlackMessage()}
                  className={`flex-1 px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
                <button
                  onClick={sendSlackMessage}
                  disabled={isSending || !slackMessageText.trim()}
                  className="px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl flex items-center gap-2 disabled:opacity-50"
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Message Detail (Gmail-style) */}
        {selectedSlackMessage && (
          <div className={`w-[620px] flex-shrink-0 flex flex-col ml-auto min-h-0 h-full ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-xl rounded-2xl overflow-hidden`}>
            {/* Close button header */}
            <div className="flex items-center justify-between px-6 pt-4">
              <button
                onClick={() => setSelectedSlackMessage(null)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
              </button>
            </div>

            {/* Message Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {/* Channel label */}
              <span
                className="inline-block px-3 py-1 rounded text-sm font-semibold text-white mb-4"
                style={{ backgroundColor: '#7C3AED' }}
              >
                #{selectedChannel?.name}
              </span>

              {/* Sender info card */}
              <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-dark-700' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full ${getAvatarColor(getSlackUserName(selectedSlackMessage.userId))} flex items-center justify-center text-white text-lg font-semibold`}>
                    {getSlackUserName(selectedSlackMessage.userId).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {getSlackUserName(selectedSlackMessage.userId)}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                      {new Date(selectedSlackMessage.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Message body */}
              <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                <p className={`text-base leading-relaxed whitespace-pre-wrap ${isDark ? 'text-text-secondary' : 'text-gray-700'}`}>
                  {selectedSlackMessage.text}
                </p>
              </div>

              {/* Reactions */}
              {selectedSlackMessage.reactions && selectedSlackMessage.reactions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {selectedSlackMessage.reactions.map((reaction, idx) => (
                    <span
                      key={idx}
                      className={`px-3 py-1.5 rounded-full text-sm ${isDark ? 'bg-dark-700 text-text-secondary' : 'bg-gray-100 text-gray-700'}`}
                    >
                      :{reaction.name}: {reaction.count}
                    </span>
                  ))}
                </div>
              )}

              {/* Thread replies indicator */}
              {selectedSlackMessage.replyCount && selectedSlackMessage.replyCount > 0 && (
                <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'border-border bg-dark-700' : 'border-gray-200 bg-gray-50'}`}>
                  <p className={`text-sm ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                    {selectedSlackMessage.replyCount} {selectedSlackMessage.replyCount === 1 ? 'reply' : 'replies'} in thread
                  </p>
                </div>
              )}
            </div>

            {/* Reply section */}
            <div className={`p-4 border-t ${isDark ? 'border-border' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Reply to this message..."
                  value={slackMessageText}
                  onChange={(e) => setSlackMessageText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      setSelectedThread(selectedSlackMessage);
                      sendSlackMessage();
                    }
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
                <button
                  onClick={() => {
                    setSelectedThread(selectedSlackMessage);
                    sendSlackMessage();
                  }}
                  disabled={isSending || !slackMessageText.trim()}
                  className="px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl flex items-center gap-2 disabled:opacity-50"
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Discord Dashboard Component (Gmail-style layout)
  const DiscordDashboard = () => {
    const filteredChannels = discordChannels.filter((channel) =>
      channel.name?.toLowerCase().includes(discordSearchQuery.toLowerCase())
    );

    // Format message date
    const formatMessageDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    // Get avatar color based on username
    const getAvatarColor = (name: string) => {
      const colors = [
        'bg-indigo-500',
        'bg-purple-500',
        'bg-blue-500',
        'bg-green-500',
        'bg-pink-500',
        'bg-orange-500',
        'bg-teal-500',
        'bg-rose-500',
      ];
      const index = name.charCodeAt(0) % colors.length;
      return colors[index];
    };

    return (
      <div className="flex h-[calc(100vh-64px)] pt-6 px-6 gap-6 overflow-hidden">
        {/* Left Panel - Message List (Gmail-style) */}
        <div className={`flex flex-col min-h-0 ${selectedDiscordMessage ? 'w-[520px] flex-shrink-0' : 'flex-1 max-w-2xl mx-auto'}`}>
          {/* Header */}
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (selectedDiscordMessage) {
                    setSelectedDiscordMessage(null);
                  } else if (selectedDiscordChannel) {
                    setSelectedDiscordChannel(null);
                    setDiscordMessages([]);
                  } else if (selectedGuild) {
                    setSelectedGuild(null);
                    setDiscordChannels([]);
                  }
                }}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-white/60'} ${!selectedGuild && !selectedDiscordChannel && !selectedDiscordMessage ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!selectedGuild && !selectedDiscordChannel && !selectedDiscordMessage}
              >
                <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
              </button>

              {/* Server dropdown selector */}
              <div className="relative">
                <button
                  onClick={() => setShowDiscordGuildDropdown(!showDiscordGuildDropdown)}
                  className={`flex items-center gap-2 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  {selectedGuild?.icon ? (
                    <img src={selectedGuild.icon} alt={selectedGuild.name} className="w-6 h-6 rounded-full" />
                  ) : (
                    <MessageSquare className="w-5 h-5" />
                  )}
                  {selectedGuild?.name || 'Select Server'}
                  <ChevronDown className={`w-5 h-5 transition-transform ${showDiscordGuildDropdown ? 'rotate-180' : ''} ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
                </button>

                {/* Guild dropdown menu */}
                {showDiscordGuildDropdown && (
                  <div className={`absolute top-full left-0 mt-2 w-64 rounded-xl shadow-lg py-1 z-50 max-h-80 overflow-y-auto ${isDark ? 'bg-dark-700 border border-border' : 'bg-white border border-gray-200'}`}>
                    {isLoadingDiscord && discordGuilds.length === 0 ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                      </div>
                    ) : discordGuilds.length === 0 ? (
                      <p className={`text-sm text-center py-4 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                        No servers found
                      </p>
                    ) : (
                      discordGuilds.map((guild) => (
                        <button
                          key={guild.id}
                          onClick={() => {
                            handleGuildSelect(guild);
                            setShowDiscordGuildDropdown(false);
                            setSelectedDiscordMessage(null);
                          }}
                          className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                            selectedGuild?.id === guild.id
                              ? isDark ? 'bg-dark-600 text-white' : 'bg-indigo-50 text-indigo-600'
                              : isDark ? 'text-text-secondary hover:bg-dark-600' : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {guild.icon ? (
                            <img src={guild.icon} alt={guild.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className={`w-8 h-8 rounded-full ${getAvatarColor(guild.name)} flex items-center justify-center text-white text-xs font-semibold`}>
                              {guild.name.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <span className="truncate">{guild.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Channel dropdown (when server selected) */}
              {selectedGuild && (
                <div className="relative">
                  <button
                    onClick={() => setShowDiscordChannelDropdown(!showDiscordChannelDropdown)}
                    className={`flex items-center gap-2 text-lg font-medium ${isDark ? 'text-text-secondary' : 'text-gray-600'}`}
                  >
                    <Hash className="w-4 h-4" />
                    {selectedDiscordChannel?.name || 'Select Channel'}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDiscordChannelDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Channel dropdown menu */}
                  {showDiscordChannelDropdown && (
                    <div className={`absolute top-full left-0 mt-2 w-64 rounded-xl shadow-lg py-1 z-50 max-h-80 overflow-y-auto ${isDark ? 'bg-dark-700 border border-border' : 'bg-white border border-gray-200'}`}>
                      {/* Search in dropdown */}
                      <div className={`px-3 py-2 border-b ${isDark ? 'border-border' : 'border-gray-100'}`}>
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-dark-600' : 'bg-gray-100'}`}>
                          <Search className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
                          <input
                            type="text"
                            placeholder="Search channels..."
                            value={discordSearchQuery}
                            onChange={(e) => setDiscordSearchQuery(e.target.value)}
                            className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder-text-muted' : 'text-gray-900 placeholder-gray-400'}`}
                          />
                        </div>
                      </div>
                      {isLoadingDiscord ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                        </div>
                      ) : filteredChannels.length === 0 ? (
                        <p className={`text-sm text-center py-4 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                          No channels found
                        </p>
                      ) : (
                        filteredChannels.map((channel) => (
                          <button
                            key={channel.id}
                            onClick={() => {
                              handleDiscordChannelSelect(channel);
                              setShowDiscordChannelDropdown(false);
                              setSelectedDiscordMessage(null);
                            }}
                            className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                              selectedDiscordChannel?.id === channel.id
                                ? isDark ? 'bg-dark-600 text-white' : 'bg-indigo-50 text-indigo-600'
                                : isDark ? 'text-text-secondary hover:bg-dark-600' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Hash className="w-4 h-4" />
                            <span className="truncate">{channel.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadDiscordGuilds}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-white/60 text-gray-500'}`}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              {selectedDiscordChannel && (
                <button
                  onClick={() => setShowMembersPanel(!showMembersPanel)}
                  className={`p-2 rounded-lg transition-colors ${
                    showMembersPanel
                      ? isDark ? 'bg-dark-700 text-white' : 'bg-white/80 text-gray-900'
                      : isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-white/60 text-gray-500'
                  }`}
                  title="Toggle members"
                >
                  <Users className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Message List - Card style */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-sm`}>
              {!selectedGuild ? (
                <div className="text-center py-16">
                  <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    Select a server to get started
                  </p>
                </div>
              ) : !selectedDiscordChannel ? (
                <div className="text-center py-16">
                  <Hash className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    Select a channel to view messages
                  </p>
                </div>
              ) : isLoadingDiscord ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
              ) : discordMessages.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    No messages in this channel
                  </p>
                </div>
              ) : (
                [...discordMessages].reverse().map((message) => {
                  const userName = message.author.globalName || message.author.username;
                  const isSelected = selectedDiscordMessage?.id === message.id;

                  return (
                    <div
                      key={message.id}
                      onClick={() => setSelectedDiscordMessage(message)}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b ${
                        isDark ? 'border-dark-700' : 'border-gray-100'
                      } ${
                        isSelected
                          ? isDark ? 'bg-dark-700' : 'bg-indigo-50'
                          : isDark ? 'hover:bg-dark-700/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full ${message.author.avatar ? '' : getAvatarColor(userName)} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 overflow-hidden`}>
                        {message.author.avatar ? (
                          <img src={message.author.avatar} alt={userName} className="w-full h-full object-cover" />
                        ) : (
                          userName.charAt(0).toUpperCase()
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {userName}
                            </span>
                            {message.author.isBot && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-500 text-white rounded">
                                BOT
                              </span>
                            )}
                          </div>
                          <span className={`text-xs flex-shrink-0 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                            {formatMessageDate(message.timestamp)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${isDark ? 'text-text-secondary' : 'text-gray-600'}`}>
                          {message.content || (message.hasAttachments ? `📎 ${message.attachmentCount} attachment${message.attachmentCount > 1 ? 's' : ''}` : 'No content')}
                        </p>
                        {message.isReply && (
                          <span className={`text-xs mt-1 inline-flex items-center gap-1 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            <Reply className="w-3 h-3" /> Reply
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Message input at bottom of left panel */}
          {selectedDiscordChannel && (
            <div className={`mt-4 p-4 rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-sm`}>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder={`Message #${selectedDiscordChannel.name}`}
                  value={discordMessageText}
                  onChange={(e) => setDiscordMessageText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendDiscordMessage()}
                  className={`flex-1 px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
                <button
                  onClick={sendDiscordMessage}
                  disabled={isSending || !discordMessageText.trim()}
                  className="px-4 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl flex items-center gap-2 disabled:opacity-50"
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Message Detail (Gmail-style) */}
        {selectedDiscordMessage && (
          <div className={`w-[620px] flex-shrink-0 flex flex-col ml-auto min-h-0 h-full ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-xl rounded-2xl overflow-hidden`}>
            {/* Close button header */}
            <div className="flex items-center justify-between px-6 pt-4 flex-shrink-0">
              <button
                onClick={() => setSelectedDiscordMessage(null)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-gray-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-gray-400'}`} />
              </button>
            </div>

            {/* Message Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
              {/* Server & Channel label */}
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="inline-block px-3 py-1 rounded text-sm font-semibold text-white"
                  style={{ backgroundColor: '#6366F1' }}
                >
                  {selectedGuild?.name}
                </span>
                <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                  #{selectedDiscordChannel?.name}
                </span>
              </div>

              {/* Sender info card */}
              <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-dark-700' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full ${selectedDiscordMessage.author.avatar ? '' : getAvatarColor(selectedDiscordMessage.author.globalName || selectedDiscordMessage.author.username)} flex items-center justify-center text-white text-lg font-semibold overflow-hidden`}>
                    {selectedDiscordMessage.author.avatar ? (
                      <img src={selectedDiscordMessage.author.avatar} alt={selectedDiscordMessage.author.username} className="w-full h-full object-cover" />
                    ) : (
                      (selectedDiscordMessage.author.globalName || selectedDiscordMessage.author.username).charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {selectedDiscordMessage.author.globalName || selectedDiscordMessage.author.username}
                      </p>
                      {selectedDiscordMessage.author.isBot && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-500 text-white rounded">
                          BOT
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                      {new Date(selectedDiscordMessage.timestamp).toLocaleString()}
                      {selectedDiscordMessage.editedAt && ' (edited)'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reply preview if message is a reply */}
              {selectedDiscordMessage.isReply && selectedDiscordMessage.replyTo && (
                <div className={`p-3 rounded-lg mb-4 border-l-4 border-indigo-500 ${isDark ? 'bg-dark-700' : 'bg-gray-100'}`}>
                  <p className={`text-xs mb-1 ${isDark ? 'text-text-muted' : 'text-gray-500'}`}>
                    Replying to {selectedDiscordMessage.replyTo.authorUsername}
                  </p>
                  <p className={`text-sm ${isDark ? 'text-text-secondary' : 'text-gray-600'}`}>
                    {selectedDiscordMessage.replyTo.content}
                  </p>
                </div>
              )}

              {/* Message body */}
              <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                <p className={`text-base leading-relaxed whitespace-pre-wrap ${isDark ? 'text-text-secondary' : 'text-gray-700'}`}>
                  {selectedDiscordMessage.content}
                </p>
              </div>

              {/* Attachments */}
              {selectedDiscordMessage.hasAttachments && (
                <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'border-border bg-dark-700' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <Paperclip className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-gray-500'}`} />
                    <span className={`text-sm ${isDark ? 'text-text-secondary' : 'text-gray-600'}`}>
                      {selectedDiscordMessage.attachmentCount} attachment{selectedDiscordMessage.attachmentCount > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}

              {/* Pinned indicator */}
              {selectedDiscordMessage.isPinned && (
                <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                  <Star className="w-3 h-3" />
                  Pinned message
                </div>
              )}
            </div>

            {/* Reply section */}
            <div className={`p-4 border-t ${isDark ? 'border-border' : 'border-gray-200'}`}>
              <div className={`flex items-center gap-2 mb-2 text-xs ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                <Reply className="w-3 h-3" />
                <span>Replying to {selectedDiscordMessage.author.username}</span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Reply to this message..."
                  value={discordMessageText}
                  onChange={(e) => setDiscordMessageText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendDiscordMessage()}
                  className={`flex-1 px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
                <button
                  onClick={sendDiscordMessage}
                  disabled={isSending || !discordMessageText.trim()}
                  className="px-4 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl flex items-center gap-2 disabled:opacity-50"
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle URL parameters for deep linking from Inbox
  useEffect(() => {
    if (urlPlatform && ['gmail', 'slack', 'discord'].includes(urlPlatform)) {
      setActivePlatform(urlPlatform);
      // Load the appropriate data based on platform
      if (urlPlatform === 'gmail') {
        loadEmails();
      } else if (urlPlatform === 'slack') {
        loadSlackChannels();
      } else if (urlPlatform === 'discord') {
        loadDiscordGuilds();
      }
    }
  }, [urlPlatform]);

  // Pre-select email from URL parameter after emails load
  useEffect(() => {
    if (urlEmailId && emails.length > 0 && !selectedEmail) {
      const email = emails.find(e => e.id === urlEmailId);
      if (email) {
        setSelectedEmail(email);

        // Mark email as read if it's unread
        if (email.isUnread && token) {
          const markAsRead = async () => {
            try {
              const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
              await fetch(`${API_URL}/communication/gmail/messages/${email.id}/mark${wsParam}`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ action: 'read' }),
              });

              // Update local state to reflect the change
              setEmails(prevEmails =>
                prevEmails.map(e =>
                  e.id === email.id ? { ...e, isUnread: false } : e
                )
              );
            } catch (error) {
              console.error('Failed to mark email as read:', error);
            }
          };
          markAsRead();
        }

        // Clear URL params after selection
        setSearchParams({});
      }
    }
  }, [urlEmailId, emails, selectedEmail, setSearchParams, token, currentWorkspace?.id]);

  // Pre-select Slack channel from URL parameter after channels load
  useEffect(() => {
    if (urlChannelId && slackChannels.length > 0 && !selectedChannel) {
      const channel = slackChannels.find(c => c.id === urlChannelId);
      if (channel) {
        setSelectedChannel(channel);
        loadSlackMessages(channel.id);
        // Clear URL params after selection
        setSearchParams({});
      }
    }
  }, [urlChannelId, slackChannels, selectedChannel, setSearchParams]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Render based on connection status
  return (
    <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50'}`}>
      {/* Content */}
      {hasConnectedCommunication ? (
        // Show platform-specific dashboard based on active platform
        activePlatform === 'gmail' && gmailIntegration ? (
          <EmailDashboard />
        ) : activePlatform === 'slack' && slackIntegration ? (
          <SlackDashboard />
        ) : activePlatform === 'discord' && discordIntegration ? (
          <DiscordDashboard />
        ) : gmailIntegration ? (
          <EmailDashboard />
        ) : slackIntegration ? (
          <SlackDashboard />
        ) : discordIntegration ? (
          <DiscordDashboard />
        ) : (
          <EmailDashboard />
        )
      ) : (
        <div className={`max-w-4xl mx-auto ${isDark ? 'bg-dark-800' : 'bg-white'} rounded-2xl shadow-sm mt-6 mx-4`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={onboardingStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {onboardingStep === 0 && <OnboardingStep1 />}
              {onboardingStep === 1 && <OnboardingStep2 />}
              {onboardingStep === 2 && <OnboardingStep3 />}
              {onboardingStep === 3 && <OnboardingStep4 />}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
